import { createAsyncThunk } from '@reduxjs/toolkit';
// The thunks file, not the slice's index: that one pulls in the slice, and
// with it notistack.
import { fetchImageFloorplanPlacements } from 'store/floorplanPlacements/thunks/floorplanPlacements.thunks';
import { loadProjectFloorplans } from 'store/floorplans/thunks/loadProjectFloorplans.thunk';

const isUnderWayOrDone = (load, projectId) =>
  !!load &&
  load.projectId === projectId &&
  (load.status === 'loading' || load.status === 'loaded');

/**
 * Loads a project's floorplans and its photos' placements on them for the
 * bank's gallery view (the Piirustus facet, the Kartta map and the pin disc
 * on the cards). The two loads are the shared slices' own thunks, run side
 * by side, so the data lands where the media viewer and the old gallery read
 * it too. How the load went is the bank slice's to keep
 * (`bankFloorplansLoad`, the latest request winning): this thunk's pending,
 * fulfilled and rejected. It rejects when either load fails, and it does
 * not run again for a project whose load is under way or done: the slice
 * marks the data stale when it has to be loaded again.
 * @param {string} projectId
 */
export const loadBankFloorplans = createAsyncThunk(
  'projectBank/loadBankFloorplans',
  async (projectId, { dispatch, rejectWithValue }) => {
    const results = await Promise.all([
      dispatch(loadProjectFloorplans(projectId)),
      dispatch(fetchImageFloorplanPlacements({ projectId })),
    ]);
    const failed = results.find((result) => !result || result.error);
    if (failed) {
      const message =
        (failed && typeof failed.payload === 'string' && failed.payload) ||
        (failed && failed.error && failed.error.message) ||
        'Failed to load the floorplans';
      return rejectWithValue({ projectId, message });
    }
    return { projectId };
  },
  {
    condition: (projectId, { getState }) => {
      if (!projectId) return false;
      const state = getState();
      const load =
        state && state.projectBank && state.projectBank.bankFloorplansLoad;
      return !isUnderWayOrDone(load, projectId);
    },
  }
);
